import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';

import { StandortService, QrOpts } from '../../services/standort.service';
import { ObjektStrukturService } from '../../services/objekt-struktur.service';

/** Minimal shape the dialog needs; both RoomIndikator and the standort-edit
 *  RoomIndicator interfaces are assignable to this. */
export interface QrIndicatorItem {
  id: string;
  name?: string;
  indikator_number?: number;
  system_id?: number;
}

export interface QrPrintDialogData {
  standortId: string;
  indicators?: QrIndicatorItem[];
}

@Component({
  selector: 'app-qr-print-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatRadioModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './qr-print-dialog.component.html',
  styleUrls: ['./qr-print-dialog.component.scss']
})
export class QrPrintDialogComponent implements OnInit {
  indicators: QrIndicatorItem[] = [];
  selected = new Set<string>();

  layout: 'compact' | 'standard' = 'compact';
  content: 'indicator' | 'website' = 'indicator';

  loading = true;
  working = false;
  error: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<QrPrintDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: QrPrintDialogData,
    private standortService: StandortService,
    private objektStrukturService: ObjektStrukturService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    if (this.data.indicators?.length) {
      this.indicators = this.data.indicators;
      this.loading = false;
      return;
    }
    this.objektStrukturService.getRoomIndikatorenByStandort(this.data.standortId).subscribe({
      next: (indicators) => {
        this.indicators = indicators || [];
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading indicators for QR dialog:', err);
        this.error = 'Fehler beim Laden der Indikatoren';
        this.loading = false;
      }
    });
  }

  get allSelected(): boolean {
    return this.indicators.length > 0 && this.selected.size === this.indicators.length;
  }

  get someSelected(): boolean {
    return this.selected.size > 0 && !this.allSelected;
  }

  toggle(id: string, checked: boolean): void {
    if (checked) {
      this.selected.add(id);
    } else {
      this.selected.delete(id);
    }
  }

  toggleAll(checked: boolean): void {
    this.selected.clear();
    if (checked) {
      this.indicators.forEach((i) => this.selected.add(i.id));
    }
  }

  onContentChange(): void {
    // Website variant always uses the large/standard visual.
    if (this.content === 'website') {
      this.layout = 'standard';
    }
  }

  formatNumber(n?: number): string {
    return n != null ? String(n).padStart(3, '0') : '000';
  }

  private buildOpts(): QrOpts {
    const opts: QrOpts = { layout: this.layout, content: this.content };
    const ids = Array.from(this.selected);
    // Empty selection or "all" => no ids (server prints all).
    if (ids.length > 0 && ids.length < this.indicators.length) {
      opts.ids = ids;
    }
    return opts;
  }

  preview(): void {
    this.working = true;
    this.standortService.printQRIndicatorLabels(this.data.standortId, this.buildOpts()).subscribe({
      next: (html) => {
        this.working = false;
        const win = window.open('', '_blank');
        if (win) {
          win.document.open();
          win.document.write(html);
          win.document.close();
        } else {
          this.snackBar.open('Popup wurde blockiert. Bitte Popups erlauben.', 'Schließen', { duration: 5000 });
        }
      },
      error: (err) => {
        this.working = false;
        console.error('Error generating QR preview:', err);
        this.snackBar.open('Fehler beim Erstellen der Vorschau', 'Schließen', { duration: 5000 });
      }
    });
  }

  downloadPdf(): void {
    this.working = true;
    this.standortService.generateQRIndicatorLabelsPDF(this.data.standortId, this.buildOpts()).subscribe({
      next: (pdfBlob: Blob) => {
        this.working = false;
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `qr-labels-standort-${this.data.standortId}-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.snackBar.open('QR Labels PDF wird heruntergeladen', 'Schließen', { duration: 3000 });
      },
      error: (err) => {
        this.working = false;
        console.error('Error generating QR PDF:', err);
        this.snackBar.open('Fehler beim Generieren der PDF', 'Schließen', { duration: 5000 });
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
